import { DateText, DetailList } from './ContentBase';
import type {
  EPPOCDetailContainer,
  EPPOCDetailsContentSource,
  EPPOCDetailsRecord,
} from '../../../types';

const EPPOC_DETAIL_FIELDS = [
  ['BPI Pain Severity', 'bpipainseverity'],
  ['BPI Pain Interference', 'bpipaininterference'],
  ['DASS Total', 'dasstotal'],
  ['DASS Depression', 'dassdepression'],
  ['DASS Anxiety', 'dassanxiety'],
  ['DASS Stress', 'dassstress'],
  ['PCS', 'pcstotal'],
  ['PSEQ', 'pseqscore'],
  ['Pain duration', 'pain_duration'],
  ['Pain count', 'paincount'],
  ['Phenotype', 'phenotype'],
] as const;

type EPPOCFieldKey = (typeof EPPOC_DETAIL_FIELDS)[number][1];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const hasEPPOCDetails = (value: unknown): value is EPPOCDetailsRecord =>
  isRecord(value) &&
  EPPOC_DETAIL_FIELDS.some(([_, key]) => value[key] !== undefined);

const isEPPOCDetailContainer = (
  value: unknown,
): value is EPPOCDetailContainer => isRecord(value);

const extractEPPOCRows = (value: unknown): EPPOCDetailsRecord[] => {
  if (isEPPOCDetailContainer(value) && Array.isArray(value.raw?.rows)) {
    return value.raw.rows.filter(hasEPPOCDetails).map((row) => ({
      ...row,
      phenotype: (row as any)?.phenotype ?? (value as any)?.phenotype,
    }));
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => extractEPPOCRows(item));
  }

  if (hasEPPOCDetails(value)) {
    return [value];
  }

  return [];
};

const resolveEPPOCRows = (
  insightDetailsContent: EPPOCDetailsContentSource,
): EPPOCDetailsRecord[] => {
  const candidates = [
    insightDetailsContent?.detail,
    insightDetailsContent?.data,
    insightDetailsContent?.content,
    insightDetailsContent?.answers,
    insightDetailsContent?.payload,
    insightDetailsContent?.survey,
    insightDetailsContent,
  ];

  for (const candidate of candidates) {
    const matchedRows = extractEPPOCRows(candidate);
    if (matchedRows.length > 0) {
      return matchedRows;
    }
  }

  return [];
};

const EPPOC_VALUE_FORMATTERS: Partial<
  Record<EPPOCFieldKey, (value: unknown) => string>
> = {
  phenotype: (value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'string') return value || '-';

    if (isRecord(value)) {
      const modalClass = Number((value as any)?.modalClass);
      const labels = [
        'Moderate symptom severity',
        'High symptom severity',
        'Low symptom severity',
      ];

      const index = Number.isFinite(modalClass) ? modalClass - 1 : -1;
      const label =
        labels[index] ??
        `Phenotype ${Number.isFinite(modalClass) ? modalClass : ''}`.trim();
      if (label) return label;

      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }

    return String(value);
  },
};

const formatEPPOCValue = (key: EPPOCFieldKey, value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const formatter = EPPOC_VALUE_FORMATTERS[key];
  return formatter ? formatter(value) : String(value);
};

interface EPPOCDetailsContentProps {
  insightDetailsContent: EPPOCDetailsContentSource;
}

const EPPOCDetailsContent = ({
  insightDetailsContent,
}: EPPOCDetailsContentProps) => {
  const rows = resolveEPPOCRows(insightDetailsContent);

  if (rows.length === 0) {
    const emptyDetails = Object.fromEntries(
      EPPOC_DETAIL_FIELDS.map(([label, key]) => [
        label,
        formatEPPOCValue(key, ''),
      ]),
    );

    return (
      <>
        <DateText
          date={
            insightDetailsContent?.event_date ??
            insightDetailsContent?.date_completed ??
            ''
          }
        />
        <DetailList details={emptyDetails} />
      </>
    );
  }
  return (
    <>
      {rows.map((row, index) => {
        const displayDate =
          insightDetailsContent?.event_date ??
          insightDetailsContent?.date_completed ??
          row?.date_completed;
        const details = Object.fromEntries(
          EPPOC_DETAIL_FIELDS.map(([label, key]) => [
            label,
            formatEPPOCValue(key, row?.[key]),
          ]),
        );

        return (
          <div
            key={`eppoc-row-${row.record_id ?? row.date_completed ?? index}`}
          >
            <DateText date={displayDate ?? ''} />
            <DetailList details={details} />
          </div>
        );
      })}
    </>
  );
};

export default EPPOCDetailsContent;
